import type { TFunction } from 'i18next';
import {
  Bike, Coffee, Flower2, LucideIcon, QrCode, ShoppingBag, UtensilsCrossed,
} from 'lucide-react';

import { TaskCategoryColors, taskCategoryIcons } from '@/views/Reports/queries/types';

import { StaffSectionGroupId, StaffSectionKey } from '../utils/staffPermissions';

/**
 * Wygląd sekcji Staff w formularzu zespołu: ikony kategorii zgłoszeń te same co w
 * zgłoszeniach panelu (`taskCategoryIcons`), żeby recepcja rozpoznawała kafelki.
 */
const ORDER_AND_TOOL_ICONS: Record<'roomService' | 'spa' | 'store' | 'rental' | 'breakfast' | 'qrScanner', LucideIcon> = {
  roomService: UtensilsCrossed,
  spa: Flower2,
  store: ShoppingBag,
  rental: Bike,
  breakfast: Coffee,
  qrScanner: QrCode,
};

export const getStaffSectionIcon = (key: StaffSectionKey): { Icon: LucideIcon; color?: string } => {
  if (key in taskCategoryIcons) {
    const taskKey = key as keyof typeof taskCategoryIcons;
    return { Icon: taskCategoryIcons[taskKey], color: TaskCategoryColors[taskKey] };
  }
  return { Icon: ORDER_AND_TOOL_ICONS[key as keyof typeof ORDER_AND_TOOL_ICONS] };
};

export const getStaffSectionLabel = (key: StaffSectionKey, t: TFunction): string => {
  switch (key) {
    case 'technical': return String(t('reports.category.technical', 'Techniczne'));
    case 'housekeeping': return String(t('reports.category.housekeeping', 'Housekeeping'));
    case 'wakeup': return String(t('reports.category.wakeup', 'Budzenie'));
    case 'guestAsk': return String(t('reports.category.guestAsk', 'Prośba gościa'));
    case 'airportTransfer': return String(t('reports.category.airportTransfer', 'Transfer na lotnisko'));
    case 'taxi': return String(t('reports.category.taxi', 'Taksówka'));
    case 'lateCheckout': return String(t('reports.category.lateCheckout', 'Późne wymeldowanie'));
    case 'minibar': return String(t('reports.category.minibar', 'Uzupełnienie'));
    case 'pillowMenu': return String(t('reports.category.pillowMenu', 'Menu poduszek'));
    case 'others': return String(t('reports.category.others', 'Inne'));
    case 'roomService': return String(t('team.staff.sections.roomService', 'Room Service'));
    case 'spa': return String(t('team.staff.sections.spa', 'Spa'));
    case 'store': return String(t('team.staff.sections.store', 'Sklep'));
    case 'rental': return String(t('team.staff.sections.rental', 'Wypożyczalnia'));
    case 'breakfast': return String(t('team.staff.sections.breakfast', 'Lista śniadaniowa'));
    case 'qrScanner': return String(t('team.staff.sections.qrScanner', 'Skaner QR'));
    default: return key;
  }
};

export const getStaffGroupLabel = (group: StaffSectionGroupId, t: TFunction): string => {
  switch (group) {
    case 'tasks': return String(t('team.staff.groups.tasks', 'Zarządzaj zgłoszeniami'));
    case 'orders': return String(t('team.staff.groups.orders', 'Zamówienia i rezerwacje'));
    default: return String(t('team.staff.groups.tools', 'Narzędzia operacyjne'));
  }
};
